import { useState } from 'react';
import { isNil } from 'lodash-es';
import { canUpgradeTo, getCurrentPrice, notes } from '../../company/billing/billing.utils';
import { useStripePricesSuspenseQuery, useStripeProductSuspenseQuery } from '../../graphql/react-operations';
import { Box } from '@startuptools/ui/Box';
import { Stack } from '@startuptools/ui/Stack';
import { Sheet } from '@startuptools/ui/Sheet';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import { Skeleton } from '@startuptools/ui/Skeleton';
import { StripeCurrencyFormat } from '@startuptools/ui/StripeCurrencyFormat';
import { stripeProducts, stripeSubPrices } from '../../../environments/environment';
import { isPromise } from '../../helpers/is-promise';
import { Trans, useLingui } from '@lingui/react';
import { type GqlCompanySubscriptionQuery } from '../../company/billing/Subscriptions.graphql';
import { BulletPoints } from '../../company/billing/BulletPoints';
import { Chip } from '@startuptools/ui/Chip';

export const SubscriptionCardSkeleton = () => (
  <Stack direction="column" gap={4}>
    <Skeleton variant="rectangular" width={100} height={29}></Skeleton>
    <Skeleton variant="rectangular" width={290} height={300}></Skeleton>
  </Stack>
);

export function SubscriptionCard({
  id,
  yearly,
  onSubSelected,
  currentSubscription,
  disabled = false,
}: {
  id: string;
  yearly: boolean;
  disabled?: boolean;
  currentSubscription?: GqlCompanySubscriptionQuery['stripeCompanySubscription'];
  onSubSelected: (priceId: string, prodId: string) => Promise<unknown> | void;
}) {
  const { i18n } = useLingui();
  const { data } = useStripeProductSuspenseQuery({ variables: { id } });
  const priceQuery = useStripePricesSuspenseQuery();
  const [loading, setLoading] = useState(false);
  const product = data.stripeProduct;
  // @ts-expect-error TS2532
  const priceId = yearly ? stripeSubPrices[id].year : stripeSubPrices[id].month;

  const price = priceQuery.data.stripePrices.find(p => p.id === priceId);
  if (isNil(price)) {
    throw new Error('missing price');
  }

  const handleOnSubSelected = async (priceId: string, id: string) => {
    const ret = onSubSelected(priceId, id);
    if (isPromise(ret)) {
      setLoading(true);
      await ret.finally(() => setLoading(false));
    }
  };

  const canUpgrade = canUpgradeTo({ price, product }, currentSubscription);
  const currentPrice = getCurrentPrice(currentSubscription);
  const isCurrentProduct = currentPrice?.product === price.product;
  const priceAmount = price.unit_amount;

  if (isNil(priceAmount)) {
    throw new Error('missing price_amount');
  }
  return (
    <Sheet sx={{ height: 500, width: 340, display: 'flex' }} variant="outlined">
      <Stack direction="column" flexGrow={1} justifyContent="space-between" sx={{ flexGrow: 1, padding: 2 }} gap={2}>
        <Stack direction="column" flexGrow={1} justifyContent="space-between" gap={2}>
          <Box>
            <Typography
              level="h4"
              endDecorator={
                <>
                  {id === stripeProducts.pro && (
                    <Chip color="success" variant="outlined">
                      <Trans id="Popular" />
                    </Chip>
                  )}
                </>
              }
            >
              {product.name}
            </Typography>
            <Box minHeight="60px">
              {id === stripeProducts.pro ? (
                <>
                  <Typography level="h4" color="primary">
                    <Trans id="Quote" />
                  </Typography>
                  <Typography>{<Trans id="Always 12 months" />}</Typography>
                </>
              ) : (
                <>
                  <Typography level="h4" color="primary">
                    <StripeCurrencyFormat locale={i18n.locale} maximumFractionDigits={0} currency={price.currency}>
                      {priceAmount}
                    </StripeCurrencyFormat>
                  </Typography>
                  <Typography>{yearly ? <Trans id="Per year" /> : <Trans id="Per month for 12 months" />}</Typography>
                </>
              )}
            </Box>
            <Stack direction="column" flexGrow={1} justifyContent="space-between">
              <BulletPoints productId={id} />
            </Stack>
          </Box>
          <Box fontSize="small">{notes[id]}</Box>
        </Stack>
        {isCurrentProduct ? (
          <Button disabled color="neutral">
            <Trans id="Current" />
          </Button>
        ) : (
          canUpgrade && (
            <>
              {id === stripeProducts.pro ? (
                <Button
                  component="a"
                  slotProps={{ root: { href: 'https://bolago.com/meeting/BookDemo', target: '_blank' } }}
                >
                  <Trans id="Schedule Meeting" />
                </Button>
              ) : (
                <Button
                  fullWidth
                  onClick={() => handleOnSubSelected(priceId, id)}
                  disabled={disabled && !loading}
                  loading={loading}
                >
                  <Trans id="Upgrade" />
                </Button>
              )}
            </>
          )
        )}
      </Stack>
    </Sheet>
  );
}
